import { useEffect, useMemo, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { useT } from "../lib/LangContext";
import { openSubscribe } from "./SubscribeModalHost";
import { useIsSubscribed } from "../lib/useIsSubscribed";

// Hostnames (or hostname suffixes) that indicate the reader arrived via
// someone else's share — a highly-qualified conversion moment because
// they came looking for the *content*, not the brand. Kept broad because
// WhatsApp/Facebook/etc. use many CDN/tracking subdomains.
const SHARE_REFERRER_SUFFIXES = [
  "wa.me", "whatsapp.com",
  "facebook.com", "fb.com", "fb.me",
  "x.com", "twitter.com", "t.co",
  "t.me", "telegram.org",
  "instagram.com",
  "linkedin.com", "lnkd.in",
  "slack.com", "discord.com",
  "reddit.com", "redd.it",
];

const DISMISS_KEY = "shared_referrer_dismissed";

const detectShareReferrer = () => {
  try {
    const ref = document.referrer || "";
    if (!ref) return null;
    const host = new URL(ref).hostname.toLowerCase();
    // Ignore self-referrals (internal navigation)
    if (host === window.location.hostname) return null;
    const match = SHARE_REFERRER_SUFFIXES.find(
      (s) => host === s || host.endsWith("." + s)
    );
    return match || null;
  } catch (_) {
    return null;
  }
};

export const SharedReferrerBanner = ({ islandSlug = "all" }) => {
  const { t } = useT();
  const source = useMemo(detectShareReferrer, []);
  const isSubscribed = useIsSubscribed();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    // Read dismissal state after mount so SSR/hydration is stable and so
    // we don't briefly flash the banner during first paint.
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
    } catch (_) {
      setDismissed(false);
    }
  }, []);

  if (!source || dismissed || isSubscribed) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch (_) { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="max-w-3xl mx-auto px-4 sm:px-6 pt-4"
      data-testid="shared-referrer-banner"
    >
      <div className="rounded-xl bg-gradient-to-r from-[#0AADA0]/10 to-[#E63946]/10 border border-[#0AADA0]/20 px-4 py-3 flex items-center gap-3">
        <div className="text-[15px] shrink-0" aria-hidden="true">🎁</div>
        <button
          type="button"
          onClick={() => openSubscribe("shared_referrer_banner")}
          className="flex-1 text-left min-w-0 group"
          data-testid="shared-referrer-cta"
        >
          <div className="text-[13px] sm:text-sm text-[#1A2E35] font-medium truncate group-hover:text-[#0AADA0] transition-colors flex items-center gap-1.5">
            <span className="truncate">
              {t("shared_referrer_cta")}
            </span>
            <ArrowRight
              size={14}
              className="shrink-0 text-[#0AADA0] group-hover:translate-x-0.5 transition-transform"
            />
          </div>
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-[#6B7280] hover:text-[#1A1A1A] p-1 shrink-0"
          data-testid="shared-referrer-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};
